import React, { createContext, useContext, useCallback, useMemo } from 'react';

const SectionControlContext = createContext(null);

export function SectionControlProvider({ sections, updateSection, ensureSectionType, gridColumns, insideGridEngine, children }) {
  const resolveSection = useCallback((sectionId) => {
    const byId = sections.find(s => String(s.id) === String(sectionId));
    if (!byId) return null;
    return sections.find(s => s.section_type === byId.section_type && s?.config?.__removed !== true)
      || byId;
  }, [sections]);

  const toggleVisibility = useCallback(async (sectionId) => {
    const section = resolveSection(sectionId);
    if (section) {
      await updateSection(section.id, { is_visible: !section.is_visible }, { sectionType: section.section_type });
    }
  }, [resolveSection, updateSection]);

  const setVisibility = useCallback(async (sectionId, visible) => {
    const section = resolveSection(sectionId);
    if (section && section.is_visible !== visible) {
      await updateSection(section.id, { is_visible: visible }, { sectionType: section.section_type });
    }
  }, [resolveSection, updateSection]);

  const moveUp = useCallback(async (sectionId) => {
    // Deep-copy to avoid mutating React state objects in-place
    const sorted = sections.map(s => ({ ...s })).sort((a, b) => a.section_order - b.section_order);
    const idx = sorted.findIndex(s => String(s.id) === String(sectionId));
    if (idx <= 0) return;
    // Skip hidden sections — find the nearest visible section above
    let swapIdx = idx - 1;
    while (swapIdx >= 0 && !sorted[swapIdx].is_visible) swapIdx--;
    if (swapIdx < 0) return;
    [sorted[swapIdx], sorted[idx]] = [sorted[idx], sorted[swapIdx]];
    sorted.forEach((s, i) => { s.section_order = i + 1; });
    await updateSection(null, { reorderAll: sorted });
  }, [sections, updateSection]);

  const moveDown = useCallback(async (sectionId) => {
    // Deep-copy to avoid mutating React state objects in-place
    const sorted = sections.map(s => ({ ...s })).sort((a, b) => a.section_order - b.section_order);
    const idx = sorted.findIndex(s => String(s.id) === String(sectionId));
    if (idx < 0 || idx >= sorted.length - 1) return;
    // Skip hidden sections — find the nearest visible section below
    let swapIdx = idx + 1;
    while (swapIdx < sorted.length && !sorted[swapIdx].is_visible) swapIdx++;
    if (swapIdx >= sorted.length) return;
    [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
    sorted.forEach((s, i) => { s.section_order = i + 1; });
    await updateSection(null, { reorderAll: sorted });
  }, [sections, updateSection]);

  const getSectionByType = useCallback((sectionType) => {
    const matches = sections.filter(s => s.section_type === sectionType);
    return matches.find(s => s?.config?.__removed !== true && s.is_visible !== false)
      || matches.find(s => s?.config?.__removed !== true)
      || matches[0];
  }, [sections]);

  const getSectionPosition = useCallback((sectionType) => {
    const sorted = [...sections].sort((a, b) => a.section_order - b.section_order);
    const visible = sorted.filter(s => s.is_visible !== false);
    const visIdx = visible.findIndex(s => s.section_type === sectionType);
    return { index: visIdx, isFirst: visIdx <= 0, isLast: visIdx >= visible.length - 1 };
  }, [sections]);

  const updateConfig = useCallback(async (sectionId, configPatch) => {
    const section = resolveSection(sectionId);
    if (!section) return;
    const mergedConfig = { ...(section.config || {}), ...configPatch };
    await updateSection(section.id, { config: mergedConfig }, { sectionType: section.section_type });
  }, [resolveSection, updateSection]);

  const value = useMemo(() => ({
    sections,
    gridColumns: gridColumns || null,
    insideGridEngine: !!insideGridEngine,
    toggleVisibility,
    setVisibility,
    moveUp,
    moveDown,
    getSectionByType,
    getSectionPosition,
    updateConfig,
    ensureSectionType,
  }), [sections, gridColumns, insideGridEngine, toggleVisibility, setVisibility, moveUp, moveDown, getSectionByType, getSectionPosition, updateConfig, ensureSectionType]);

  return (
    <SectionControlContext.Provider value={value}>
      {children}
    </SectionControlContext.Provider>
  );
}

export function useSectionControl() {
  return useContext(SectionControlContext);
}
